import type { CheckedDownloadItem, DownloadTreeNode } from '~/types/download'
import { isBookPayload } from '~/utils/download/isBookPayload'

function flattenBookNodes(nodes: DownloadTreeNode[]): DownloadTreeNode[] {
  const flat: DownloadTreeNode[] = []

  const visit = (node: DownloadTreeNode) => {
    if (isBookPayload(node.payload)) {
      flat.push(node)
    }
    for (const child of node.children ?? []) {
      visit(child)
    }
  }

  for (const node of nodes) {
    visit(node)
  }

  return flat
}

function findNextBoundaryNode(flatNodes: DownloadTreeNode[], targetTocId: string, currentPageNumber?: string | null): DownloadTreeNode | null {
  const currentIndex = flatNodes.findIndex(node => isBookPayload(node.payload) && node.payload.tocId === targetTocId)

  if (currentIndex < 0) {
    return null
  }

  for (let index = currentIndex + 1; index < flatNodes.length; index++) {
    const candidate = flatNodes[index]
    if (!candidate || !isBookPayload(candidate.payload)) {
      continue
    }

    const isUnusableSamePageBoundary = currentPageNumber
      && candidate.payload.pageNumber === currentPageNumber
      && !candidate.payload.fragmentId

    if (isUnusableSamePageBoundary) {
      continue
    }

    return candidate
  }

  return null
}

export function useBookDownloadBoundries(toc: DownloadTreeNode[], items: CheckedDownloadItem[]): CheckedDownloadItem[] {
  const flatNodes = flattenBookNodes(toc)

  return items.map((item) => {
    const payload = item.payload
    if (!isBookPayload(payload)) {
      return item
    }

    const nextBoundary = findNextBoundaryNode(flatNodes, payload.tocId, payload.pageNumber)
    const nextPayload = nextBoundary?.payload
    const nextBookPayload = isBookPayload(nextPayload)
      ? nextPayload
      : null

    return {
      ...item,
      payload: {
        ...payload,
        nextPageNumber: nextBookPayload?.pageNumber ?? null,
        nextFragmentId: nextBookPayload?.fragmentId ?? null,
      },
    }
  })
}
